(ns app.front.ui.sortable-table
  (:require ["@carbon/icons-react/es/arrow--right/16" :default ArrowRight16]
            ["@carbon/icons-react/es/caret--up/20" :default CaretUp20]
            ["@carbon/icons-react/es/chevron--down/24" :default ChevronDown24]
            ["@carbon/icons-react/es/chevron--up/24" :default ChevronUp24]
            ["@carbon/icons-react/es/close/16" :default Close16]
            ["@carbon/icons-react/es/drag--vertical/16" :default DragVertical16]
            ["@carbon/icons-react/es/draggable/24" :default Draggable24]
            ["@carbon/icons-react/es/settings--adjust/16" :default SettingsAdjust16]
            [app.front.ui :as ui]
            [app.front.ui.dnd :as dnd]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.status :as ui-status]
            [app.front.ui.styled-sticky :as ui-ss]
            [app.front.ui.text-button :as ui-text-button]
            [app.front.ui.viewport-observer :as viewport-observer]
            [app.front.user-settings :as us]
            [app.front.util :refer [for-frag]]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema-tools.core :as st]
            [schema.core :as s]))


(s/defschema ColumnKey (s/cond-pre s/Str s/Keyword))


(s/defschema ColumnConfig
  (st/optional-keys
    {:config-visible? s/Bool
     :expand-default? s/Bool
     :columns         {ColumnKey s/Bool},
     :order           [ColumnKey]}))


(rf/reg-event-db ::init
  (fn [db [_ id]]
    (if id
      (let [user-data (us/get-value [:sortable-table-column-config id] ColumnConfig)]
        (assoc-in db [::column-config id] user-data))
      db)))


(rf/reg-sub ::column-config
  (fn [db [_ id]]
    (get-in db [::column-config id])))


(rf/reg-event-fx ::save-column-config
  (fn [{:keys [db]} [_ id]]
    (let [data (get-in db [::column-config id])]
      {:fx [[:dispatch [::us/save-user-settings [:sortable-table-column-config id] data ColumnConfig]]]})))


(rf/reg-event-fx ::set-column-order
  (fn [{:keys [db]} [_ id columns]]
    {:db (assoc-in db [::column-config id :order] columns)
     :fx [[:dispatch [::save-column-config id]]]}))


(rf/reg-event-fx ::set-column-visibility
  (fn [{:keys [db]} [_ id key visible?]]
    {:db (assoc-in db [::column-config id :columns key] visible?)
     :fx [[:dispatch [::save-column-config id]]]}))


(rf/reg-event-fx ::set-column-config-visible
  (fn [{:keys [db]} [_ id visible?]]
    {:db (assoc-in db [::column-config id :config-visible?] visible?)
     :fx [[:dispatch [::save-column-config id]]]}))


(rf/reg-event-fx ::reset-column-config
  (fn [{:keys [db]} [_ id]]
    {:db (assoc-in db [::column-config id] {:config-visible? true})
     :fx [[:dispatch [::save-column-config id]]]}))


(rf/reg-event-fx ::set-expand-default
  (fn [{:keys [db]} [_ id expand-default?]]
    {:db (assoc-in db [::column-config id :expand-default?] expand-default?)
     :fx [[:dispatch [::save-column-config id]]]}))


(defn do-tr [tr content]
  (if (and (keyword? content) tr)
    (tr [content])
    content))


(defn compare-tr
  "Compare keywords as translations"
  [tr c1 c2]
  (let [lang     (<sub [:language])
        collator (i18n-model/memoized-collator lang)]
    (.compare collator (do-tr tr c1) (do-tr tr c2))))


(defn- create-styles [{:keys [flex min-width max-width width word-break]
                       :or   {flex 1 min-width "0px"}}]
  (p/assoc-if
    {:flex      flex
     :min-width (or width min-width)
     :max-width (or width max-width "none")
     :width     (or width "auto")}
    :word-break word-break))


(defn- calculate-table-min-width [headers]
  (if-let [lengths (->> headers
                        (map (fn [{:keys [width min-width]}]
                               (let [length (or width min-width "0px")]
                                 (if (number? length)
                                   (str length "px")
                                   length))))
                        seq)]
    (str "max(100%, calc(" (str/join " + " lengths) "))")
    "100%"))


(defn- table-container [{:keys [id sticky-header?] :as props} & children]
  (r/with-let [measurement-id (str "sortable-table-overflow-" id)]
    (let [horizontal-overflow? (when id
                                 (<sub [::viewport-observer/horizontal-overflow? measurement-id]))]
      [:<>
       (into [:div (r/merge-props (dissoc props :sticky-header?)
                                  {:class (when (and (not sticky-header?) horizontal-overflow?)
                                            "overflow-x-auto")})]
             children)
       (when id
         [viewport-observer/observer
          {:id                measurement-id
           :element-id        id
           :observe-viewport? false}])])))


(defn- table-header-wrapper [{:keys [sticky-header?] :as props} & children]
  (let [tag       :div.flex.flex-col.z-10.mb-4.rounded-md.bg-ui-white.transition-shadow
        tag-props (dissoc props :sticky-header?)]
    (if sticky-header?
      (let [header-bottom (<sub [::viewport-observer/visible-bottom :sticky-header])
            top           (+ header-bottom 8)]
        (into [ui-ss/styled-sticky
               (merge tag-props
                      {:tag          tag
                       :class        ["lg:sticky" "sortable-table__sticky"]
                       :sticky-class ["bg-ui-white" "shadow-md" "border" "border-solid" "border-ui-gray" "-m-px"]
                       :top          top})]
              children))
      (into [tag tag-props] children))))


(defn- table-toolbar [props & children]
  (into [:div.flex.w-full.bg-ui-white.py-2.rounded-md props]
        children))


(defn- table-toolbar-actions [props & children]
  (into [:div.flex.w-full.flex-row.items-center.gap-2.flex-wrap props]
        children))


(defn- table-header-row [props & children]
  (into [:div.hidden.lg:flex.w-full.bg-gray-200.rounded-md
         (r/merge-props props {:class "sortable-table__header-row"})]
        children))


(defn- table-header-col
  "Table header column with optional sorting and actions
  `on-sort` A function that is called when the cell is clicked
  `on-sort-hook` An additional function that is called when the cell is clicked, takes the current sorting as an argument.
  `sorted`  nil, :asc or :desc"
  [{:keys [on-sort sorted on-sort-hook first-column?] :as props} & children]
  [:<>
   [(if on-sort :button :div)
    (merge {:class   ["flex flex-1 font-semibold text-sm px-2 py-3 align-left border-none leading-tight items-center"
                      "rounded-none first:group-first:rounded-l last:group-last:rounded-r"
                      (when first-column? "border-l-6 border-solid border-transparent")
                      (when on-sort "hover:bg-ui-blue-10 focus:bg-ui-violet-20 focus:z-10")
                      (when sorted "bg-gray-300")
                      (:class props)]
            :data-cy "table-header-cell"}
           (when on-sort {:on-click (if on-sort-hook
                                      #(do (on-sort %)
                                           (on-sort-hook sorted))
                                      on-sort)}))
    (into [:<>] children)
    (when sorted [:div.flex.items-center.ml-1.-my-1
                  {:style (merge {:transition "transform 200ms ease-in-out"}
                                 (when (= sorted :desc) {:transform "scaleY(-1)"}))}
                  [:> CaretUp20]])]])


(defn- table-settings-button [{:keys [on-click description]}]
  [:button.w-10.p-0.border-0.rounded-none.first:group-first:rounded-l.last:group-last:rounded-r.hover:bg-ui-blue-10.focus:bg-ui-violet-20.focus:z-10
   {:on-click   on-click
    :aria-label description
    :data-cy    "table-settings-button"}
   [:> SettingsAdjust16]])


(defn- closest-until
  "Find the closest ancestor of `el` that matches `selector` up in DOM tree until `stop-at-el`"
  [el stop-at-el selector]
  (when-not (or (nil? el) (nil? stop-at-el))
    (let [match? (.matches el selector)
          stop?  (= el stop-at-el)
          parent (.-parentElement el)]
      (cond
        stop? nil
        match? el
        parent (closest-until parent stop-at-el selector)
        :else nil))))


(defn- interactive-target?
  "Check if click event is caught inside an interactive element (input, rich text editor etc.)"
  [event *wrapper-el-ref]
  (let [target           (.-target event)
        wrapper-el       @*wrapper-el-ref
        ;; Search only *below* the row wrapper. A `:link` row is itself an <a>,
        ;; so an unbounded `.closest` would match the row for every click and
        ;; report all of them as interactive — which breaks the frontend router.
        interactive?     (or (contains? #{"INPUT" "TEXTAREA"} (.-tagName target))
                             ; These may have children, e.g. span tags:
                             (->> ["A" "LABEL" "BUTTON" "SELECT"]
                                  (some #(closest-until target wrapper-el %))))
        in-rt-editor?    (closest-until target wrapper-el ".rich-text-editor--editing")
        in-date-picker?  (closest-until target wrapper-el ".DayPickerInput")
        in-phrase-modal? (closest-until target wrapper-el ".phrase-modal")]
    (or interactive? in-rt-editor? in-date-picker? in-phrase-modal?)))


(defn- table-row-wrapper [{:keys [expanded?
                                  expandable?
                                  on-expand
                                  on-row-click
                                  has-actions?
                                  has-link?
                                  focus?
                                  href
                                  target-props
                                  on-select
                                  selecting?
                                  selected?
                                  opts
                                  key
                                  item
                                  table-min-width]} & children]
  (r/with-let [*ref (r/atom nil)]
    (let [opts-ref      (:ref opts)
          row-action?   (or expandable? has-actions? selecting?)
          row-action-fn (cond
                          row-action?
                          (fn [e]
                            (if selecting?
                              (on-select (not selected?) key)
                              (on-expand key e)))

                          on-row-click
                          (fn [_e]
                            (on-row-click item)))
          clickable?    (or expandable? has-actions? has-link? (some? on-row-click))]
      (into [(if (and has-link? (not row-action-fn)) :a :div)
             (r/merge-props {:class   (into ["flex w-full items-center text-ui-night-sky-100 no-underline bg-gray-200 my-2"
                                             "sortable-table__row"
                                             "rounded-md border-solid border-transparent border-r border-t border-b"]
                                            (if selecting?
                                              [(if selected?
                                                 "bg-ui-violet-100 text-ui-white hover:bg-ui-violet-90"
                                                 "hover:bg-ui-blue-10")
                                               "cursor-pointer select-none"]
                                              [(when clickable?
                                                 "cursor-pointer hover:bg-ui-blue-10 focus:bg-ui-violet-20")
                                               (when expanded?
                                                 "rounded-b-none bg-ui-white border-ui-gray border-b-0")
                                               (when focus?
                                                 "bg-ui-blue-10")]))
                             :data-cy "table-row"
                             :style   {:min-width table-min-width}
                             :ref     (fn [element]
                                        (reset! *ref element)
                                        (when (fn? opts-ref)
                                          (opts-ref element)))}
                            (when expandable?
                              {:aria-expanded (boolean expanded?)})
                            (if row-action-fn
                              {:on-click    (fn [e]
                                              (when-not (interactive-target? e *ref)
                                                (row-action-fn e)))
                               :on-key-down (fn [e]
                                              (when (and (contains? #{"Enter" " "} (.-key e))
                                                         (not (interactive-target? e *ref)))
                                                (.preventDefault e)
                                                (row-action-fn e)))
                               :tab-index   0
                               :role        "button"}
                              (when has-link?
                                {:href     href
                                 :on-click (fn [e]
                                             (when (interactive-target? e *ref)
                                               (.stopPropagation e)))}))
                            target-props
                            (dissoc opts :ref))]
            children))))


(defn- table-row-columns-wrapper [{:keys [row-class-override]} & children]
  (into [:div.flex.flex-1.flex-col.lg:py-0.py-3.rounded-md.mb-0.lg:flex-row
         {:class ["sortable-table__row-columns"
                  (or row-class-override "lg:items-center sortable-table__row-columns--default")]}]
        children))


(defn- table-row-first-column-wrapper [{:keys [status expanded?]} & children]
  (into [:div.flex.self-stretch.rounded-md.rounded-r-none.border-l-6.border-solid
         {:class [(or (some-> status ui-status/status->border-color-class)
                      "border-ui-dark-gray")
                  (when expanded? "rounded-b-none")]}]
        children))


(defn- table-row-checkbox [{:keys [tr on-select selected? key]}]
  [:div.flex.self-stretch.items-center.justify-center.w-14
   {:class "[&>div]:items-center"}
   (cc/checkbox {:id         (str "select_" key)
                 :label-text (tr [:select/select])
                 :hide-label true
                 :checked    (boolean selected?)
                 :on-change  (fn [checked?] (on-select checked? key))})])


(defn- row-expandable? [{:keys [expanded-content actions]}]
  (boolean (or expanded-content (seq actions))))


(defn- table-row-icon-column [{:keys [expandable? expanded? has-link?]}]
  [:div.flex.self-stretch.items-center.justify-center.w-14
   (if expandable?
     (if expanded?
       [:> ChevronUp24]
       [:> ChevronDown24])
     (when has-link? [:> ArrowRight16]))])


(defn- table-row-drag-handle [props]
  [:div.flex.self-stretch.items-center.justify-center.w-14
   (assoc props :data-cy "table-row-drag-handle")
   [:> Draggable24]])


(defn- table-row-expansion-wrapper [{:keys [status]} & children]
  [:div.border-solid.border-b.border-r.border-ui-gray.-mt-5.rounded-b-md.bg-ui-white
   (into [:div.mt-5.border-l-6.border-solid.rounded-b-md
          {:class [(or (some-> status ui-status/status->border-color-class)
                       "border-ui-dark-gray")]}]
         children)])


(defn- table-row-expanded-content [& children]
  (into [:div.px-3.py-3.border-t.border-solid.border-ui-gray
         {:data-cy "table-row-expanded-content"}]
        children))


(defn- table-row-actions [& children]
  (into [:div.flex.flex-col.lg:flex-row.lg:flex-wrap.lg:justify-end.px-4.gap-2.py-4.border-t.border-ui-gray.border-solid
         {:data-cy "table-row-actions"}]
        children))


(defn- action-button [tr {:keys [confirm? confirm-content on-click title disabled? active?] :as action}]
  (if confirm?
    [ui/modal-confirmation-button
     (util/mmap {:trigger-button-opts (merge (st/select-schema action ui/ModalConfirmationTriggerButtonOpts)
                                             {:size :small
                                              :kind :tertiary})
                 :trigger-button-text title
                 :on-yes-click        on-click
                 :disabled?           (boolean disabled?)}
                tr)
     [:p confirm-content]]
    [ui/button
     (merge {:size :small
             :kind :secondary}
            (st/select-schema action cc/ButtonOptions)
            {:disabled (boolean disabled?)
             :active?  (boolean active?)})
     title]))


(defn- href-button [href description]
  [ui/button
   {:href href
    :size :small
    :kind :secondary}
   description])


(defn- column [{:keys [headers sub-header? headerless?]} i {:keys [opts content]}]
  (let [{col-title :title} (get headers i)
        header-col?        (and headers (not headerless?))
        common-class       ["[&_input[type='text']]:!bg-ui-white"
                            "[&_input[type='email']]:!bg-ui-white"
                            "[&_input[type='number']]:!bg-ui-white"
                            "[&_input[type='search']]:!bg-ui-white"
                            "[&_input[type='password']]:!bg-ui-white"
                            "[&_select]:!bg-ui-white"]]
    ^{:key (str key "-col-" i)}
    [:<>
     ;; Small view cell
     (when content
       [:div.md:flex.md:items-center.lg:hidden
        {:class ["sortable-table__small-column"
                 (or (:col-class opts) "block md:flex md:flex-1 sortable-table__small-column--default")
                 (when (or sub-header?
                           (> i 0))
                   (if headerless? "mt-2" "mt-4"))]}
        (when header-col?
          [:div.mb-2.md:mb-0.font-bold.leading-tight.w-full.md:w-1-4.md:min-w-1-4.md:mr-2.text-ui-night-sky.uppercase.tracking-wide.text-xs.lg:hidden.lg:w-full
           {:class [(when sub-header? "hidden")
                    "sortable-table__small-column-label"]}
           col-title])
        [:div
         (merge (dissoc opts :col-class)
                {:class   [common-class
                           (if header-col?
                             "w-3-4"
                             "w-full")]
                 :data-cy "table-column"})
         content]])
     ;; Large view cell
     [:div.hidden.mb-4.px-2.lg:block.lg:mb-0.py-3.break-words.sortable-table__large-column
      (-> opts
          (assoc :class common-class)
          (assoc :style (create-styles (get headers i))
                 :data-cy "table-column")
          (dissoc :col-class))
      content]]))


(defn- table-row [{:keys [expanded?
                          expandable?
                          has-expandable-rows?
                          row-class-override
                          on-expand
                          on-row-click
                          on-select
                          selection
                          selecting?
                          headers
                          headerless?
                          has-link?
                          table-min-width
                          tr]}
                  {:keys [expanded-content key cols opts sub-header? link actions focus?
                          status target-props draggable-props item]}]
  (let [expanded?                  (and expandable?
                                        (expanded? key))
        selected?                  (contains? selection key)
        {:keys [description href]} link
        has-actions?               (boolean (seq actions))]
    (when-not key
      (js/console.warn
        "Missing React key (check your idfunc returns a value or a key is provided manually) for a table row with cols:"
        (pr-str cols)))
    ^{:key key}
    [:<>
     [table-row-wrapper (util/mmap expanded?
                                   expandable?
                                   on-expand
                                   on-row-click
                                   has-actions?
                                   has-link?
                                   focus?
                                   href
                                   target-props
                                   on-select
                                   selecting?
                                   selected?
                                   opts
                                   key
                                   item
                                   table-min-width)
      [table-row-first-column-wrapper
       (util/mmap status expanded?)
       (cond
         draggable-props
         [table-row-drag-handle draggable-props]

         selecting?
         [table-row-checkbox (util/mmap selected?
                                        on-select
                                        tr
                                        key)]

         (or expandable? has-expandable-rows? has-link?)
         [table-row-icon-column (util/mmap expandable? expanded? has-link?)])]
      [table-row-columns-wrapper (util/mmap row-class-override)
       (->> cols
            (filter some?)
            (map-indexed (partial column (util/mmap headers sub-header? headerless?)))
            doall)]]
     (when expanded?
       ^{:key (str key "-expansion")}
       [table-row-expansion-wrapper
        (util/mmap status)
        (when expanded-content
          [table-row-expanded-content expanded-content])
        (when (or has-actions? href)
          [table-row-actions
           (for-frag [action actions]
             [action-button tr action])
           (when href
             [href-button href description])])])]))


(defn- toolbar [{:keys [tr
                        can-reorder?
                        reordering?
                        on-toggle-reordering
                        on-clear
                        on-select-all
                        selection
                        can-select?
                        selecting?
                        perma-selecting?
                        on-toggle-selecting
                        toolbar-actions
                        table-toolbar-props]
                 :or   {table-toolbar-props {}}}]
  (let [common-close-props {:icon          Close16
                            :icon-position :right
                            :active?       true}]
    [table-toolbar table-toolbar-props
     [table-toolbar-actions {}
      (when (and can-reorder? (not selecting?))
        [action-button tr (merge {:title    (tr [(if reordering? :table/end-rearranging :table/rearrange)])
                                  :on-click on-toggle-reordering}
                                 (when reordering? common-close-props))])
      (when (and can-select? (not reordering?) (not perma-selecting?))
        [action-button tr (merge {:title    (tr [(if selecting? :table/end-select :table/select-items)])
                                  :on-click (fn []
                                              (when selecting? (on-clear))
                                              (on-toggle-selecting))}
                                 (when selecting? common-close-props))])
      (when (and selecting? (fn? on-select-all))
        [action-button tr {:title    (tr [:table/select-all])
                           :on-click on-select-all}])
      (when (and selecting? (fn? on-clear))
        ;; A text button, the selection is cleared much more rarely than the actions are used
        [action-button tr {:title         (tr [:table/clear-selection])
                           :on-click      on-clear
                           :disabled?     (empty? selection)
                           :kind          :tertiary
                           :icon          Close16
                           :icon-position :right}])
      (for-frag [action toolbar-actions]
        [action-button tr action])]]))


(defn- table-external-expand-state [{:keys [headerless?
                                            tr
                                            expanded?
                                            on-expand
                                            on-row-click
                                            on-select
                                            on-clear
                                            selection
                                            row-class-override
                                            configurable?
                                            id
                                            can-reorder?
                                            reordering?
                                            on-toggle-reordering
                                            can-select?
                                            selecting?
                                            perma-selecting?
                                            on-toggle-selecting
                                            on-select-all
                                            toolbar-actions
                                            sticky-header?
                                            table-toolbar-props]
                                     :as   opts}
                                    headers
                                    rows]
  (let [headers                    (->> headers
                                        (filter some?)
                                        vec)
        has-link?                  (some :link rows)
        has-expandable-rows?       (some row-expandable? rows)
        headers-with-expandability (cond->> headers
                                     (or has-expandable-rows? has-link?) (into [{:title "" :width "62px"}]))
        toolbar-visible?           (or can-reorder? (fn? on-clear) (seq toolbar-actions))
        header-row-visible?        (and headers (not headerless?))
        table-min-width            (calculate-table-min-width headers-with-expandability)]
    [table-container
     (r/merge-props {:class          "sortable-table-container"
                     :data-cy        "table"
                     :sticky-header? sticky-header?}
                    (select-keys opts [:class :id]))
     (when (or toolbar-visible? header-row-visible?)
       [table-header-wrapper (util/mmap sticky-header?)
        (when toolbar-visible?
          [toolbar (util/mmap tr
                              can-reorder?
                              reordering?
                              on-toggle-reordering
                              can-select?
                              selecting?
                              perma-selecting?
                              on-toggle-selecting
                              on-clear
                              on-select-all
                              selection
                              toolbar-actions
                              table-toolbar-props)])
        (when header-row-visible?
          [table-header-row {:style {:min-width table-min-width}}
           (->> headers-with-expandability
                (map-indexed (fn [idx {:keys [title class on-sort on-sort-hook sorted] :as header}]
                               ; Common parent for the clickable header column and all the action buttons next to it
                               [:div.flex.flex-1.group
                                {:style (create-styles header)}
                                [table-header-col
                                 (util/mmap {:class         (str class " pr-2")
                                             :first-column? (zero? idx)}
                                            on-sort
                                            on-sort-hook
                                            sorted)
                                 title]
                                ; Show inside the width definitions of the last column to keep the columns aligned
                                (when (and configurable? (= idx (-> headers-with-expandability count dec)))
                                  [table-settings-button {:on-click    #(>evt [::set-column-config-visible id true])
                                                          :description (tr [:table/adjustments])}])]))
                (into [:<>]))])])
     [:div
      (->> rows
           (map
             (fn [row]
               ^{:key (:key row)}
               [table-row
                (util/mmap {:expandable? (row-expandable? row)}
                           has-expandable-rows?
                           expanded?
                           row-class-override
                           on-expand
                           on-row-click
                           on-select
                           selection
                           selecting?
                           tr
                           headers
                           headerless?
                           has-link?
                           table-min-width)
                row]))
           doall
           (cons :<>)
           vec)]]))


(defn keys->set [ks]
  (cond
    (coll? ks) (set ks)
    (some? ks) #{ks}
    :else #{}))


(defn table-internal-expand-state
  "Produces a table.

   You can expand a single row by default by providing :default-expanded-key in opts. The value must correspond to a key
   provided for a table row.

   The arguments are:
   `opts`     a map optionally containing {:expandable?          bool
                                           :tr                   translation-fn
                                           :class                \"class-name\"
                                           :default-expanded-key str
                                           :row-class-override   str
                                           :headerless?          bool
                                           :id                   str} ; Does not render the title bar, but column widths
                                                                        defined in header still take effect
   `headers`  a list of table headers. Header width values are inherited by the whole column:
              {:title     string
               :opts      options for the header element
               :flex      optional flex value (default 1, every column is equally wide)
               :max-width optional max-width, any CSS width value, default none
               :min-width optional min-width, any CSS width value, default 0
               :width     optional fixed width, any CSS width value, default auto}
   `rows`     a list of maps that represent the table rows. The maps look like this:
              {:expanded-content  anything
               :key               string or keyword React key for the row
               :sub-header?       boolean, whether this row is a subheader for grouping rows
               :cols              a list of maps representing the row columns: [{:opts {...td attrs...}, :content anything},...]
               :opts              options for the row element}

  `expandable?` whether it's possible to expand a row
  `tr`          translation function"
  [{:keys [default-expanded-key]} _ _]
  (let [*expanded-rows (r/atom (keys->set default-expanded-key))
        expanded?      (fn [key] (contains? @*expanded-rows key))
        on-expand-int  (fn [key] (swap! *expanded-rows (if (expanded? key) disj conj) key))]
    (r/create-class
      {:component-did-update
       (fn [this [_ old-props] & _]
         (let [{:keys [default-expanded-key]} (r/props this)]
           (when (not= default-expanded-key (-> old-props :default-expanded-key))
             (reset! *expanded-rows (keys->set default-expanded-key)))))
       :reagent-render
       (fn [{:keys [on-expand] :as opts} headers rows]
         [table-external-expand-state
          (assoc opts
                 :expanded? expanded?
                 :on-expand (if on-expand
                              (fn [key]
                                (let [expanded? (contains? (on-expand-int key) key)]
                                  ;; only trigger on-expand if expanded, not on collapse
                                  (on-expand key expanded?)))
                              ;; if only internal expand state, do not change function ref on render
                              on-expand-int))
          headers
          rows])})))


(def common-col-opts
  [:flex :max-width :min-width :width :word-break])


(defn- col-sort-key
  "Stable identifier of a column's sorting, `:sort-key` in opts or the extractor."
  [[_ extractor opts]]
  (or (:sort-key opts) extractor))


(defn- col-with-sort-key [cols sort-key]
  (some (fn [col] (when (= sort-key (col-sort-key col)) col)) cols))


(defn- col-extractor-with-sort-key [cols sort-key]
  (second (col-with-sort-key cols sort-key)))


(defn- extract-col-content [item extractor]
  (if (fn? (extractor item))
    ((extractor item) item)
    (extractor item)))


(defn- item->cols [item cols]
  (->> cols
       (filter some?)
       (mapv (fn [[_ extractor {:keys [truncate?] :as opts}]]
               (merge {:content (extract-col-content item extractor)
                       :opts    {:class [(:class opts)
                                         (when truncate? "lg:truncate sortable-table__truncate")]}}
                      (select-keys opts common-col-opts))))))


(defn- item->row [get-focus?
                  link
                  get-status
                  get-row-opts
                  cols
                  id-func
                  expanded-func
                  make-actions
                  on-row-click
                  tr
                  augment-target-props
                  augment-draggable-props
                  item]
  (let [focus? (and get-focus? (get-focus? item))
        href   (when link ((:make-href link) item))
        status (and get-status (-> item get-status))]
    (merge {:cols   (item->cols item cols)
            :key    (id-func item)
            :focus? focus?
            :status status
            :opts   (when get-row-opts (get-row-opts item))}
           (when on-row-click
             {:item item})
           (when (and augment-target-props augment-draggable-props)
             {:target-props    (augment-target-props {} item)
              :draggable-props (augment-draggable-props {} item)})
           (when expanded-func
             {:expanded-content (expanded-func item)})
           (when make-actions
             {:actions (remove nil? (make-actions item))})
           (when href
             (let [description (:description link)]
               {:link {:href        href
                       :description (do-tr
                                      tr
                                      (if (fn? description)
                                        (description item)
                                        description))}})))))


(defn- get-column-visibility [[title _ {:keys [hidden-by-default?]}] column-config]
  (get-in column-config [:columns title] (not hidden-by-default?)))


(defn- column-selector-row [{:keys [tr id column column-config uncheck-allowed? opts drag-handle-opts]}]
  (let [title    (first column)
        checked? (get-column-visibility column column-config)]
    [:div.flex.items-center
     opts
     [:div.flex-1
      (cc/checkbox {:id         (str title)
                    :label-text (do-tr tr title)
                    :on-change  #(>evt [::set-column-visibility id title %])
                    :checked    checked?
                    :disabled   (and checked? (not uncheck-allowed?))})]
     [:div
      drag-handle-opts
      [:> DragVertical16]]]))


(defn reset-config-button [tr id]
  [ui-text-button/text-button
   {:on-click #(>evt [::reset-column-config id])}
   (tr [:table/restore-default-settings])])


(defn- column-selector [tr id cols column-config]
  (let [uncheck-allowed? (->> cols
                              (filter #(get-column-visibility % column-config))
                              rest
                              seq)
        items            (->> cols (map-indexed (fn [i col] {:ordered/order i :column col})))]
    [:div
     [dnd/wrap-dnd
      {:id         (str id "-column-selector-sortable-dnd")
       :items      items
       :on-reorder (dnd/wrap-on-order-changed
                     items
                     #(>evt [::set-column-order id (->> % (map (comp first :column)))]))}
      (fn [{:keys [sorted augment-draggable-props augment-target-props]}]
        (for-frag [{:keys [column] :as item} sorted]
          [column-selector-row (util/mmap {:opts             (augment-target-props {} item)
                                           :drag-handle-opts (augment-draggable-props {} item)}
                                          tr id column column-config uncheck-allowed?)]))]]))


(defn- expand-default-toggle [tr id expand-default?]
  [:div.border-b.border-t.border-solid.border-ui-gray.py-2
   (cc/checkbox
     {:id         (str "sortable-table-expand-default-toggle-" id)
      :label-text (tr [:table/toggle-expand-default])
      :checked    (boolean expand-default?)
      :on-change  #(>evt [::set-expand-default id %])})])


(defn- column-config-modal [tr id cols {:keys [config-visible? expand-default?] :as column-config} expandable?]
  (let [on-close          #(>evt [::set-column-config-visible id false])
        configurable-cols (->> cols
                               (filter #(->> % first ((every-pred some? (complement str/blank?))))))]
    (cc/composed-modal
      {:open       config-visible?
       :on-close   on-close
       :class-name "[&>div]:max-w-sm"}
      (cc/modal-header {:close-modal on-close
                        :title       (tr [:table/adjustments])})
      (cc/modal-body {}
                     [:div
                      [eb/error-boundary
                       [:div.flex.flex-col.gap-4
                        [reset-config-button tr id]
                        (when expandable? [expand-default-toggle tr id expand-default?])
                        [column-selector tr id configurable-cols column-config]]]])
      (cc/modal-footer {}
                       [ui/button
                        {:kind     :primary
                         :on-click on-close}
                        (tr [:close])]))))


(defn- wrap-dnd-if-needed [{:keys [id items on-reorder reordering?]} component-fn]
  (if (and (fn? on-reorder) reordering?)
    [dnd/wrap-dnd (util/mmap id items on-reorder) component-fn]
    (component-fn)))


(defn table
  "A sortable, configurable data table with responsive styling.

   Renders a table with support for sorting, column configuration, row expansion,
   row selection, drag-and-drop reordering, and sticky headers.

  `:id` - unique identifier for the table, only required if on-reorder or configurable? is given
          NOTE: required for persisting column configuration to user settings
  `:cols` - tuples of header (keyword, string or component)
                    + extractor (a fn item -> component)
                    + opts (optional, can have
                               * `:truncate?` boolean that controls wrapping,
                               * `:sorting` controls whether the column is sortable and how:
                                   - missing/nil/false: column is not sortable, no click handler on header
                                   - `true`: column is sortable using locale-aware string comparison
                                             (via `Intl.Collator`), keywords are translated before comparison
                                   - custom comparator fn: column is sortable using the provided
                                             `(fn [item1 item2] ...)` comparator function
                               * `:on-sort-hook` fn [current-sorting] called when column header is clicked,
                               * `:hidden-by-default?` boolean, column hidden unless user enables it,
                               * `:class` CSS class string for the column cells,
                               and the CSS passthrough props `:width`, `:flex`, `:min-width`, `:max-width`, `:word-break`)
  `:items` - collection of items to render as rows
  `:sort-items?` - defaults to true; false preserves the supplied row order while keeping
                   sort indicators and header callbacks, for externally sorted results
  `:tr` - translation function (fn [[keyword]] -> string), used for i18n and locale-aware sorting
  `:id-func` - item -> row key (defaults to `:db/id`)
  `:class` - CSS class string for the table container
  `:headerless?` - boolean, hides the header row but column widths still apply
  `:row-class-override` - additional CSS classes applied to the row's columns wrapper (not the full row element)
  `:link` - map w/ `:make-href` function item -> url and `:description` keyword, string or function item -> string
  `:make-actions` - function item -> vector of action maps for the row's action buttons. Action map keys:
                    `:title` button text, `:on-click` handler, `:disabled?` boolean, `:active?` boolean,
                    `:confirm?` boolean (shows confirmation modal), `:confirm-content` modal content
  `:get-focus?` - function item -> boolean that highlights the row and initially expands it when expandable
  `:get-status` - function item -> `:critical`, `:warning`, `:attention`, `:notification`, `:task` or `:ok` or `nil`
  `:get-row-opts` - function item -> HTML props map applied to the row wrapper
  `:on-select` - function [checked? key] called when a row is selected/deselected
  `:perma-selecting?` - boolean, renders the row checkboxes immediately instead of behind the
                        toolbar's 'Select items' toggle
  `:on-clear` - function called to clear the current selection
  `:on-select-all` - function called to select all currently relevant rows
  `:selection` - set of selected row keys
  `:on-reorder` - function [item-from item-to] for reordering items via drag-and-drop
                  NOTE: requires `:id`
  `:expanded-func` - function item -> component for rendering expanded content for each row
  `:expanded-row-keys` - set of keys/ids of rows that should be expanded, for external expansion state control
  `:on-expand` - function [key expanded?] called when a row is expanded (NOT on collapse), `expanded?` is
                 always `true`. Mandatory with :expanded-row-keys for external state management
  `:on-row-click` - function item -> nil, called when a row is clicked. Makes the row clickable with
                    cursor-pointer and hover styling. When `:expanded-func` or `:make-actions` are also provided,
                    fires only for rows without expanded content and without row actions. Takes precedence over `:link`
                    (the row renders as a div, not an anchor, so `:link` becomes ineffective)
  `:configurable?` - boolean, enables column configuration modal (visibility, order, expand-by-default)
                     NOTE: requires unique `:id`
  `:initial-sorting` - vector of [sort-key :asc/:desc] to set the initial sorting state, where sort-key is
                       the column's `:sort-key` opt or, when not given, the column's extractor. Columns with a
                       function extractor should provide a stable `:sort-key` (e.g. a keyword) in their opts, so
                       the sorted column keeps its identity when the extractor is recreated on re-render. A
                       column's `:sorting` comparator, when it has one, is used for the initial sort too
  `:toolbar-actions` - vector of action maps (same structure as `:make-actions`) to display in the table toolbar
  `:sticky-header?` - boolean, makes the header stick to the top when scrolling
  `:table-toolbar-props` - map of props to pass to the table toolbar component, e.g. `{:class \"my-class\"}`"
  [{:keys [row-class-override
           expanded-func
           headerless?
           tr
           class
           on-expand
           on-row-click
           on-select
           on-clear
           on-select-all
           selection
           perma-selecting?
           cols
           get-focus?
           get-row-opts
           link
           get-status
           id-func
           make-actions
           items
           sort-items?
           expanded-row-keys
           configurable?
           initial-sorting
           id
           on-reorder
           toolbar-actions
           sticky-header?
           table-toolbar-props]
    :or   {sort-items? true}}]
  (when (and configurable? (not id))
    (js/console.warn
      "sortable-table with configurable? parameter should always have an id"))
  (when (and on-row-click link)
    (js/console.warn
      "sortable-table: :on-row-click takes precedence over :link — rows will render as divs, not anchors"))
  (r/with-let [_        (rf/dispatch [::init id])
               ;; The sorting state is [extractor desc? sort-comparator sort-key], where sort-key is the
               ;; column's stable `:sort-key` (or its extractor when no `:sort-key` is given). The extractor
               ;; is resolved from the current cols by sort-key, so recreating function extractors on
               ;; re-render does not lose the identity of the sorted column.
               *sorting (r/atom (if initial-sorting
                                  (let [sort-key                            (first initial-sorting)
                                        [_ col-extractor {:keys [sorting]}] (col-with-sort-key cols sort-key)
                                        extractor                           (or col-extractor sort-key)
                                        comparator                          (if (fn? sorting) sorting true)]
                                    (case (second initial-sorting)
                                      :asc [extractor false comparator sort-key]
                                      :desc [extractor true comparator sort-key]))
                                  [nil nil]))
               *reordering? (r/atom false)
               on-toggle-reordering #(swap! *reordering? not)
               *selecting? (r/atom false)
               on-toggle-selecting #(swap! *selecting? not)]
    (let [[sorted-by desc? sort-comparator sort-key] @*sorting

          ;; `perma-selecting?` can turn on after the table has mounted, e.g. when the data it
          ;; depends on loads asynchronously, so it is read on every render instead of only as the
          ;; initial value of `*selecting?`. The toggle that flips `*selecting?` is hidden while
          ;; perma-selecting, so the states cannot conflict.
          perma-selecting? (boolean perma-selecting?)
          selecting?       (or perma-selecting? @*selecting?)

          column-config   (<sub [::column-config id])
          column-order    (:order column-config)
          expand-default? (:expand-default? column-config)
          ordered-cols    (if column-order
                            (->> cols
                                 ; Columns without title cannot be sorted, keep them at the end of the list:
                                 (sort-by #(let [i (.indexOf column-order (first %))]
                                             (if (= i -1) js/Number.MAX_VALUE i))))
                            cols)
          visible-cols    (->> ordered-cols
                               (filter #(get-column-visibility % column-config)))
          id-func         (or id-func :db/id)
          expandable?     (some? expanded-func)
          ;; Re-resolve the extractor from the current cols: function extractors may be recreated
          ;; on re-render, the sort-key keeps the identity stable.
          sorted-by       (if sort-key
                            (or (col-extractor-with-sort-key cols sort-key) sorted-by)
                            sorted-by)]
      [:<>
       (wrap-dnd-if-needed
         (util/mmap {:reordering? @*reordering?} id items on-reorder)
         (fn [{:keys [augment-draggable-props augment-target-props sorted]}]
           [(if expanded-row-keys
              table-external-expand-state
              table-internal-expand-state)
            (util/mmap {:can-reorder?         (fn? on-reorder)
                        :reordering?          @*reordering?
                        :can-select?          (fn? on-select)
                        :selecting?           selecting?
                        :perma-selecting?     perma-selecting?
                        :has-actions?         (some? make-actions)
                        :expanded?            (when expanded-row-keys
                                                #(contains? expanded-row-keys %))
                        :default-expanded-key (when expandable?
                                                (cond
                                                  expand-default? (map id-func items)
                                                  get-focus? (->> items
                                                                  (filter get-focus?)
                                                                  (map id-func))))}
                       expandable?
                       row-class-override
                       headerless?
                       tr
                       class
                       on-expand
                       on-row-click
                       on-select
                       on-clear
                       on-select-all
                       selection
                       configurable?
                       id
                       on-toggle-reordering
                       on-toggle-selecting
                       toolbar-actions
                       sticky-header?
                       table-toolbar-props)
            (->> visible-cols
                 (filter some?)
                 (mapv (fn [[title extractor {:keys [sorting on-sort-hook sort-key] :as opts}]]
                         (let [[_ desc? _ sorted-key] @*sorting
                               sort-key               (or sort-key extractor)
                               sorted?                (= sorted-key sort-key)]
                           (merge {:title (do-tr tr title)}
                                  (when sorting
                                    {:on-sort #(reset! *sorting [extractor (and sorted? (not desc?)) sorting sort-key])})
                                  (when sorted?
                                    {:sorted (if desc? :desc :asc)})
                                  (when on-sort-hook
                                    {:on-sort-hook on-sort-hook})
                                  (select-keys opts common-col-opts))))))
            (cond->> (or sorted items)
              (and sort-items? sorted-by)
              (sort (if (true? sort-comparator)
                      #(compare-tr tr
                                   (extract-col-content %1 sorted-by)
                                   (extract-col-content %2 sorted-by))
                      sort-comparator))
              (and sort-items? desc?) reverse
              :always (mapv (partial item->row
                                     get-focus?
                                     link
                                     get-status
                                     get-row-opts
                                     visible-cols
                                     id-func
                                     expanded-func
                                     make-actions
                                     on-row-click
                                     tr
                                     augment-target-props
                                     augment-draggable-props)))]))
       (when configurable?
         [column-config-modal tr id ordered-cols column-config expandable?])])))
